import { Injectable } from "@angular/core";
import { v4 as uuid } from 'uuid';
import { LiveChatStateService } from "./live-chat-state.service";
import { Attendance, AttendanceChannel, ContactChannel } from "../interfaces/attendance";
import {
  AttendanceContentType,
  AttendanceMessage,
  AttendanceMessageContent,
  AttendanceMessageStatus,
  AttendanceMessageType,
  MediaReplyType,
  MetaData
} from "../interfaces/attendance-list-history.interface";
import { MediaTypes } from "../interfaces/media-types.enum";
import { MediaPayload } from "../interfaces/media-payload.interface";
import { WhatsappInvitationPayload } from "../interfaces/whatsapp-invitation.interface";
import { AttendanceStatus } from "../interfaces/attendance-status.enum";

type PreviewContent = string | MediaPayload | WhatsappInvitationPayload;

@Injectable({
  providedIn: 'root',
})
export class LiveChatSendMessagePreviewService {
  private messageType!: AttendanceContentType;
  private frontendMessageId!: string;
  private messageContent: AttendanceMessageContent = {};
  private contactChannel!: ContactChannel;

  constructor(
    private liveChatState: LiveChatStateService,
  ) {}

  previewSendMessage(content: PreviewContent, contactChannel?: ContactChannel, attendanceMessage?: AttendanceMessage): string | undefined {
    if (contactChannel) {
      this.contactChannel = contactChannel;
    }

    if (!this.shouldAddPreviewSendMessage(content, contactChannel)) return;

    this.generateFrontendMessageId();
    this.resolveMessageType(content);
    this.createMessageContent(content, attendanceMessage);
    this.appendPreviewHistory();

    return this.frontendMessageId;
  }

  failedSendMessage(frontendMessageId: string) {
    this.frontendMessageId = frontendMessageId;
    this.resolveMessageType('');
    this.updatePreviewHistoryToFailed();
  }

  markAsFailed(metadata: MetaData): void {
    const history = { ...this.generateHistory(true), metadata };
    this.liveChatState.updateAttendanceHistory(history);
  }

  private shouldAddPreviewSendMessage(content: PreviewContent, contactChannel?: ContactChannel): boolean {
    let channelReferenceIdToCheck: string | undefined;

    if (typeof content === 'string') {
      channelReferenceIdToCheck = contactChannel?.channelReferenceId;
    } else if (content.type === AttendanceContentType.LIST_BUTTONS) {
      channelReferenceIdToCheck = content.whatsappInvitation.channelReferenceId;
    } else {
      channelReferenceIdToCheck = content.channelReferenceId;
    }

    const notShowPreviewFromChannel = contactChannel?.channel && [AttendanceChannel.BOOKING, AttendanceChannel.EXPEDIA].includes(contactChannel?.channel);

    if (notShowPreviewFromChannel && this.currentAttendance().status === AttendanceStatus.ARCHIVED) {
      return false;
    }

    return channelReferenceIdToCheck === this.currentAttendance()?.currentContactChannel?.channelReferenceId;
  }

  private resolveMessageType(content: PreviewContent) {
    this.messageType = (typeof content === 'string') ? AttendanceContentType.TEXT : this.resolveMediaType(content.type);
  }

  private resolveMediaType(mediaType: MediaTypes | AttendanceContentType): AttendanceContentType {
    if (mediaType === AttendanceContentType.LIST_BUTTONS) return mediaType;
    if (mediaType === MediaTypes.AUDIO) return AttendanceContentType.AUDIO;
    if (mediaType === MediaTypes.IMAGE) return AttendanceContentType.IMAGE;
    if (mediaType === MediaTypes.VIDEO) return AttendanceContentType.VIDEO;
    return AttendanceContentType.DOCUMENT;
  }

  private createMessageContent(content: PreviewContent, attendanceMessage?: AttendanceMessage) {
    if (typeof content === 'string') {
      this.messageContent = { text: content };
    } else if (content.type === AttendanceContentType.LIST_BUTTONS) {
      this.createMessageContentListButtons(content);
    } else {
      this.createMessageContentMedia(content);
    }
    this.addReplyDataIfNeeded(attendanceMessage);
  }

  private createMessageContentMedia(content: MediaPayload) {
    this.messageContent = {
      media: {
        isUploading: true,
        // The real (post-send) attendant-sent bubble reads mediaText, not
        // caption (chat-bubble-media-cta) — match that here so the caption
        // doesn't just pop in once the send finishes.
        ...(content.mediaText ? { mediaText: content.mediaText } : {}),
      },
    };
  }

  private createMessageContentListButtons(content: WhatsappInvitationPayload) {
    if(content.whatsappInvitation?.linkOnly){
      this.messageContent = {
        text: content.whatsappInvitation.textDialog,
      };
    }else{
      this.messageContent = {
        text: content.whatsappInvitation.textDialog,
        attachments: [
          {
            content: {
              buttons: [
                {
                  title: content.whatsappInvitation.textButton,
                  type: '',
                  value: ''
                }
              ]
            }
          }
        ]
      };
    }
  }

  private addReplyDataIfNeeded(attendanceMessage?: AttendanceMessage) {
    if (attendanceMessage?.data) {
      const { type, content, status } = attendanceMessage.data;
      this.messageContent.reply = {
        type: type as MediaReplyType,
        content,
        name: '',
        status: status as AttendanceMessageStatus,
      }
      this.messageContent.canBeReplied = false;
    }
  }

  private appendPreviewHistory() {
    const attendance = this.currentAttendance();
    const history = this.generateHistory();
    this.liveChatState.addNewHistoryMessages(attendance.id, history);
  }

  updatePreviewHistoryToFailed() {
    const history = this.generateHistory(true);
    this.liveChatState.updateAttendanceHistory(history);
  }

  private generateHistory(isFailed?: boolean): AttendanceMessage {
    const attendance = this.currentAttendance();
    const currentContactChannel = this.contactChannel || attendance.currentContactChannel

    return {
      channel: currentContactChannel.channel,
      type: AttendanceMessageType.MESSAGE,
      attendant: attendance.currentAttendant,
      frontendMessageId: this.frontendMessageId,
      data: {
        status: !isFailed ? AttendanceMessageStatus.AWAITING : AttendanceMessageStatus.FAILED,
        type: this.messageType,
        content: this.messageContent,
        isSender: true,
      },
      createdAt: new Date().toISOString(),
    } satisfies AttendanceMessage;
  }

  private generateFrontendMessageId(): void {
    this.frontendMessageId = uuid();
  }

  private currentAttendance(): Attendance {
    return this.liveChatState.currentAttendance as Attendance;
  }
}
